import { Alert, Box, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { ISpec, ISpecCategory, ISpecOption } from '../../../../models';
import { AddOption } from '../AddSpecOption';
import { MobileCategoryOptions } from '../MobileCategoryOptions';
import { Table } from '../../../../components';
import { SALES_PORTAL_PER_PAGE_OPTIONS, getEnvironment } from '../../../../helpers';
import { useContext, useMemo } from 'react';
import { SalesContext, update } from '../../../../context/sales';

interface IManageTenDayOptions {
  isFetchingOptionsCategories: boolean;
  hasCorrectRole: () => boolean;
  specOptionsCategories: ISpecCategory[];
  editableTenDayCategoryOptions: ISpecOption[];
  setEditableTenDayCategoryOptions: (options: ISpecOption[]) => void;
  setOverrideExistingSelection: (val: boolean) => void;
  overrideExistingSelection: boolean;
  spec: ISpec;
  getColumns: (isContract?: boolean) => any[];
  editingId: string;
  setEditingId: (val: string) => void;
  isFetchingOptions: boolean;
  updateOptionsData: (
    option: ISpecOption,
    columnId: string,
    value: string,
    error?: boolean,
    isContract?: boolean
  ) => void;
  isPrint?: boolean;
}

const PAGE_NAME = 'Ten-Day-Options';
export const ManageTenDayOptions: React.FC<IManageTenDayOptions> = ({
  isFetchingOptionsCategories,
  hasCorrectRole,
  specOptionsCategories,
  editableTenDayCategoryOptions,
  setEditableTenDayCategoryOptions,
  setOverrideExistingSelection,
  overrideExistingSelection,
  spec,
  getColumns,
  editingId,
  setEditingId,
  isFetchingOptions,
  updateOptionsData,
  isPrint = false
}) => {
  // Filter out options that are flagged display false
  const filterEditableTenDayCategoryOptions = editableTenDayCategoryOptions.filter(
    option => !option.display
  );
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);

  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () =>
      currentPageState && currentPageState >= SALES_PORTAL_PER_PAGE_OPTIONS[0]
        ? currentPageState
        : SALES_PORTAL_PER_PAGE_OPTIONS[0],
    [currentPageState]
  );

  const setRowsPerPage = (itemsPerPage: number) => {
    return dispatchPerPage(
      update({
        pageName: PAGE_NAME,
        environment: getEnvironment(),
        itemsPerPage: itemsPerPage
      })
    );
  };
  return (
    <div key={'ten-day-options-tab-content'}>
      <Box displayPrint="block">
        <Typography variant="h6" gutterBottom paddingTop="1rem">
          Ten Day Options
        </Typography>
      </Box>
      {!isFetchingOptionsCategories && hasCorrectRole() && (
        <AddOption
          categories={specOptionsCategories.filter(cat => !cat.displayName.includes('Contract'))}
          options={filterEditableTenDayCategoryOptions}
          setOptions={setEditableTenDayCategoryOptions}
          setOverride={setOverrideExistingSelection}
          override={overrideExistingSelection}
        />
      )}
      {spec.missingTenDayCategories === null && (
        <Alert severity="success">Ten Day Option Selections Complete</Alert>
      )}
      {spec.missingTenDayCategories !== null && (
        <Alert severity="error">Ten Day Option Selections Incomplete</Alert>
      )}
      <Table
        columns={getColumns(false).filter(col => {
          if (!hasCorrectRole()) {
            return col.id !== 'actions';
          }
          return true;
        })}
        data={filterEditableTenDayCategoryOptions}
        isLoading={isFetchingOptions}
        useTableProps={{
          updateMyData: (option: ISpecOption, columnId: string, value: string, error?: boolean) =>
            updateOptionsData(option, columnId, value, error, false)
        }}
        hidePagination={isPrint}
        mobileProps={{
          options: filterEditableTenDayCategoryOptions,
          setOptions: setEditableTenDayCategoryOptions,
          editingId: editingId,
          handleEdit: (val: ISpecOption) => {
            const isEditing = editingId === val.optionId;
            isEditing ? setEditingId(null) : setEditingId(val.optionId);
          },
          handleDelete: (val: ISpecOption) => {
            const result = window.confirm('Are you sure you want to remove this item?');
            if (result) {
              setEditableTenDayCategoryOptions(
                editableTenDayCategoryOptions.filter(option => val.optionId !== option.optionId)
              );
            } else {
              return;
            }
          }
        }}
        rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
        rowsPerPage={perPage}
        setRowsPerPage={setRowsPerPage}
        ResponsiveComponent={isMobile ? MobileCategoryOptions : undefined}
        scrollToTopOnPaging
      />
    </div>
  );
};
